/*******************************************************************************
 * Copyright (c) 2023 Belltane
 *
 * All rights reserved.
 *
 * Contributors:
 *   
 *  Amina Rais - Initial implementation
 *  Version: 1.0.0
 *
 *******************************************************************************/
import { useState } from 'react';
import { Box, Grid, Typography, Button, Link } from '@mui/material'
import { getFunctions, httpsCallable } from "firebase/functions";
import CircularProgress from '@mui/material/CircularProgress';
import { TextField } from 'formik-mui'
import { makeStyles } from 'tss-react/mui';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup'
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@emotion/react';

const RegisterPage = () => {
    const [error, setError] = useState('');
    const navigate = useNavigate()
    const { classes } = useStyles();
    const [loading, setLoading] = useState(false)
    const theme = useTheme();
    const handleRegister = async (user, { setSubmitting }) => {
        const { email, password, displayName } = user;

        //  Reset state
        setError(null);
        setLoading(true);

        try {
            if (!email || !password || !displayName) {
                throw new Error("All fields are required.");
            }

            const functions = getFunctions();
            const createAdminUser = httpsCallable(functions, "createAdminUser");

            const result = await createAdminUser({ email, password, displayName });

            if (!result?.data?.success) {
                throw new Error(result?.data?.message);
            }


            setError("");
            navigate("/login");
        } catch (err) {
            console.error("Registration error:", err);

            //   Map Firebase/Functions errors to human messages
            let message = "Registration failed. Please try again.";

            if (err.code) {
                switch (err.code) {
                    case "functions/invalid-argument":
                        message = "Invalid data. Please check your input.";
                        break;
                    case "functions/already-exists":
                    case "auth/email-already-exists":
                        message = "An account with this email already exists.";
                        break;
                    case "functions/internal":
                        message = "Internal server error. Please try later.";
                        break;
                    default:
                        message = "Invalid data. Please check your input.";
                }
            } else if (err.message) {
                message = err.message;
            }

            setError(message);
        } finally {
            setSubmitting(false);
            setLoading(false);
        }
    };


    return (
        <Box className={classes.containerBox}>
            <Box className={classes.backgroundImage} />

            <Box className={classes.BoxContainerLogin}>
                <Typography className={classes.heading}>
                    Sign Up To Bula Instant Bets Admin
                </Typography>

                <Grid container >

                    <Grid item xs={12} sm={12} md={12}>
                        {error && error !== "" && (
                            <Typography className={classes.alert}  >
                                {error}
                            </Typography>
                        )}
                        <Formik
                            initialValues={{
                                email: '',
                                password: '',
                            }}

                            validationSchema={Yup.object().shape({
                                email: Yup.string().email().required('Email is required.'),
                                password: Yup.string().min(8).max(30).required('Password is required.'),
                                confirmPassword: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords must match.').required('Confirm password is required.'),
                                displayName: Yup.string().required('Display name is required.'),
                            })}
                            onSubmit={(
                                { email, password, confirmPassword, displayName },
                                { setSubmitting }
                            ) => {
                                handleRegister({ email, password, confirmPassword, displayName }, { setSubmitting });
                            }}
                        >
                            {() => (
                                <Form>
                                    <Field
                                        name="displayName"
                                        label="Display Name"
                                        type="text"
                                        component={TextField}
                                        margin="normal"
                                        variant="standard"
                                        fullWidth
                                    />
                                    <Field
                                        name="email"
                                        label="email"
                                        type="email"
                                        component={TextField}
                                        margin="normal"
                                        variant="standard"
                                        fullWidth
                                    />


                                    <Field
                                        name="password"
                                        label="Password"
                                        type="password"
                                        component={TextField}
                                        margin="normal"
                                        variant="standard"
                                        fullWidth
                                    />
                                    <Field
                                        name="confirmPassword"
                                        label="Confirm Password"
                                        type="password"
                                        component={TextField}
                                        margin="normal"
                                        variant="standard"
                                        fullWidth
                                    />
                                    <Grid container spacing={6} >
                                        <Grid item xs={12} sm={12} md={12}>
                                            <Button
                                                variant='contained'
                                                fullWidth
                                                type='submit'
                                                className={classes.button}
                                                disabled={loading}
                                            >
                                                {loading && <CircularProgress className={classes.progress} color="success" size={24} />}
                                                <Typography variant='h3' color="white"> Register</Typography>

                                            </Button>
                                            <Box textAlign='center' mt={3}>
                                                <Typography
                                                    variant='body2'
                                                    style={{ color: '#94a3b8', marginBottom: '8px' }}
                                                >
                                                    Or
                                                </Typography>
                                                <Button
                                                    component={Link}
                                                    fullWidth
                                                    to='/login'
                                                    variant='outlined'
                                                    mt={3}

                                                    style={{
                                                        color: '#fff',
                                                        borderColor: '#475569',
                                                        textTransform: 'none',
                                                        fontWeight: 500,
                                                        padding: '8px 24px',
                                                        borderRadius: '8px',
                                                        '&:hover': {
                                                            borderColor: theme.palette.primary.main,
                                                            color: theme.palette.primary.main,
                                                        },
                                                    }}
                                                    onClick={() => { navigate("/login") }}  >
                                                    <Typography variant='h4'>  Log In</Typography>
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Form>

                            )}

                        </Formik>


                    </Grid>

                </Grid>


            </Box>
        </Box>
    );
};

const useStyles = makeStyles({ name: 'RegisterPage' })(theme => ({
    button: {
        marginTop: theme.spacing(3),
        color: '#fff',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
        },
        borderRadius: 8,
        height: 50,
        fontWeight: 600,
        fontSize: '1rem',
    },
    containerBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        background: theme.palette.background.paper,
        padding: theme.spacing(2),
    },
    BoxContainerLogin: {
        padding: theme.spacing(4),
        borderRadius: 16,
        backdropFilter: 'blur(15px)',
        backgroundColor: '#332F30',
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        width: '100%',
        maxWidth: 480,
        color: '#f1f5f9',
    },
    backgroundImage: {
        position: 'absolute',
        top: 100,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        filter: 'grayscale(100%)',
        backgroundImage: `url('/assets/images/bg/bg.svg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
    },
    logo: {
        display: 'block',
        margin: '0 auto 20px auto',
        height: 120,
    },
    heading: {
        textAlign: 'center',
        fontWeight: 700,
        fontSize: '1.8rem',
        marginBottom: theme.spacing(3),
        color: '#e0f2fe',
    },
    inputField: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.primary.main,
            color: '#e2e8f0',
            '& fieldset': {
                borderColor: theme.palette.primary.main,
            },
            '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
            },
        },
        '& .MuiInputLabel-root': {
            color: '#94a3b8',
            '&.Mui-focused': {
                color: theme.palette.primary.main,
            },
        },
    },
    alert: {
        marginBottom: theme.spacing(2),
        backgroundColor: '#b91c1c',
        color: '#fef2f2',
    },
}));


export default RegisterPage;
